import { getToken } from "@utils/customHooks/useOauth";

/**
 * Browser client for the PSA lookups that cannot be cached.
 *
 * boards, statuses, priorities, templates and template tickets all come from
 * the daily `psa_integrations.settings` cache that the page embeds. These four
 * cannot: companies is a typeahead over the customer's whole PSA, and
 * available-projects/phases/tickets are scoped to a PSA project the user has
 * only just picked. They used to be served by SowBuilder actions that called
 * the PSA from this host, which the PSA does not allow-list; now the browser
 * asks scopestack-api's v2 endpoints, and SowBuilder is out of the path.
 *
 * Every failure resolves to an empty list. A dropdown with nothing in it is
 * the same outcome the flag-off page already produced when the PSA was
 * unreachable, and it keeps a dead PSA from breaking the rest of the form.
 */
export interface PsaV2Row {
  id: string;
  name: string;
}

type QueryParams = Record<string, string | undefined | null>;

const fetchRows = async (
  integrationId: string,
  resource: string,
  params: QueryParams
): Promise<PsaV2Row[]> => {
  // No integration row means the account's reference cache has never been
  // written, so there is nothing to address and no point asking.
  if (!integrationId) return [];

  try {
    const url = new URL(
      `${process.env.REACT_APP_SCOPESTACK_API_HOST}/v2/psa-integrations/${integrationId}/${resource}`
    );
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, value);
      }
    });

    // Shared with every React root on the page: one cached token, one
    // in-flight grant. See the coalescing in useOauth.
    const token = await getToken();
    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    });
    if (!response.ok) return [];

    const body = await response.json();
    return ((body && body.data) || []).map(
      (row: { id?: unknown; name?: string }) => ({
        id: String(row.id ?? ""),
        name: row.name ?? "",
      })
    );
  } catch {
    return [];
  }
};

export const psaCompanies = (
  integrationId: string,
  startsWith: string
): Promise<PsaV2Row[]> =>
  fetchRows(integrationId, "companies", { starts_with: startsWith });

export const psaAvailableProjects = (
  integrationId: string,
  scope: { companyId?: string; boardId?: string }
): Promise<PsaV2Row[]> =>
  fetchRows(integrationId, "available-projects", {
    company_id: scope.companyId,
    board_id: scope.boardId,
  });

export const psaProjectPhases = (
  integrationId: string,
  projectId: string
): Promise<PsaV2Row[]> =>
  fetchRows(integrationId, "project-phases", { project_id: projectId });

export const psaProjectTickets = (
  integrationId: string,
  projectId: string
): Promise<PsaV2Row[]> =>
  fetchRows(integrationId, "project-tickets", { project_id: projectId });

/**
 * Company typeahead for the server-rendered form.
 *
 * Local first: a client the account has already mapped to this connection is a
 * DB read and must not cost a PSA round-trip. Only when nothing local matches
 * does this reach the PSA for similarly-named companies -- the case where the
 * project's client carries no id for this integration at all. That lookup used
 * to happen server-side on page load, unbounded and paginated; it is now on
 * demand, scoped to what the user actually typed, and made by scopestack-api.
 */
export const psaCompanySearch = async (
  integrationId: string,
  localUrl: string,
  serviceUuid: string,
  term: string
): Promise<{ id: string; text: string }[]> => {
  let local: { id: string; text: string }[];
  try {
    const url = new URL(localUrl, window.location.origin);
    url.searchParams.set("term", term);
    url.searchParams.set("service", serviceUuid);

    const response = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return [];

    const body = await response.json();
    local = (body && body.results) || [];
  } catch {
    return [];
  }

  if (local.length > 0) return local;

  const rows = await psaCompanies(integrationId, term);
  return rows.map((row) => ({ id: row.id, text: row.name }));
};
